import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import ProposalDomainModule, { ProposalDomainParams } from 'store/modules/proposal/proposalDomainModule';
import { createContext, useContext } from 'react';
import { getProposalGenerationState, getProposalGenerationStatus } from 'api/operations/proposalServiceV3';
import { ProposalGenerationStatus } from 'types/proposalService';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import FirstStepController from '../../ui-stores/proposal-v2/proposal-generation/firstStepController';
import SecondStepController from '../../ui-stores/proposal-v2/proposal-generation/secondStepController';

type ControllerUnion = FirstStepController | SecondStepController;

type Controllers = [FirstStepController, SecondStepController];

export default class GenerateProposalPageStore {
  private rootStore: RootStore;
  private _isLoading = false;
  private proposalDomainModule = new ProposalDomainModule();
  private _generatedFilesStatus: ProposalGenerationStatus | null = null;
  private _currentStepIndex: number = 0;
  private steps: Controllers;

  public get currentStepIndex(): number {
    return this._currentStepIndex;
  }

  public get areFilesNonExistent(): boolean {
    return this._generatedFilesStatus === ProposalGenerationStatus.NOT_EXIST;
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  private get currentStepController(): ControllerUnion {
    const controller = this.steps[this._currentStepIndex];
    if (!controller) {
      throw new Error(`Failed to get step controller! Invalid current step index: ${this._currentStepIndex}!`);
    }
    return controller;
  }

  public get isCurrentControllerValid(): boolean {
    return this.currentStepController.isValid;
  }

  public get firstStepController(): FirstStepController {
    return this.steps[0];
  }

  public get secondStepController(): SecondStepController {
    return this.steps[1];
  }

  public get currentSponsorId(): string {
    return this.proposalDomainModule.sponsorId;
  }

  public setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  private setGeneratedFilesStatus(status: ProposalGenerationStatus): void {
    this._generatedFilesStatus = status;
  }

  public setStepIndex(index: number): void {
    if (index < 0 || index > this.steps.length - 1) {
      throw new Error(`Invalid step index! Tried to assign ${index} to the list of ${this.steps.length}.`);
    }
    this._currentStepIndex = index;
  }

  public onNavigateStepBack(): void {
    if (this._currentStepIndex > 0) {
      this.setStepIndex(this._currentStepIndex - 1);
    }
  }

  private async loadInitialData(params: ProposalDomainParams): Promise<void> {
    await this.proposalDomainModule.loadProposalDomainData(params);
  }

  public async preparePage(params: ProposalDomainParams): Promise<void> {
    this.setIsLoading(true);
    await this.loadInitialData(params);

    const proposalState = await getProposalGenerationState(params.cartId);

    await runInAction(async () => {
      const status = await getProposalGenerationStatus(
        params.cartId,
        this.proposalDomainModule.proposalDomainData!.cart.lastUpdateTime,
      );
      runInAction(() => {
        this.setGeneratedFilesStatus(status);

        if (!this.areFilesNonExistent) {
          this.firstStepController.updateFormData(proposalState.generateProposalAnswers);
          this.secondStepController.updateFormData(proposalState.generateProposalAnswers);
        }
        this.setIsLoading(false);
      });
    });
  }

  private async completeCurrentStep(catalogId: string): Promise<void> {
    const controller = this.steps[this._currentStepIndex];
    if (!controller) {
      throw new Error(`No step controller with such index: ${this._currentStepIndex}`);
    }
    if (!this.proposalDomainModule.proposalDomainData) {
      throw new Error('Some of the proposal domain data has not been uploaded!');
    }
    this.setIsLoading(true);
    await controller.completeStep(
      this.proposalDomainModule.proposalDomainData,
      catalogId,
      this.rootStore.domainStoreV2.domainHeader.id,
    );
  }

  // Called on "Next" button click
  public async saveStep(catalogId: string): Promise<void> {
    await this.completeCurrentStep(catalogId);
    runInAction(() => {
      this.setIsLoading(false);
      this.setStepIndex(this._currentStepIndex + 1);
    });
  }

  // Called on "Complete" button click
  public async completeFlow(catalogId: string): Promise<void> {
    await this.completeCurrentStep(catalogId);
    runInAction(() => {
      this.setIsLoading(false);
    });
  }

  constructor() {
    this.rootStore = getRootStoreInstance();
    this.steps = [new FirstStepController(), new SecondStepController()];

    makeObservable<
      GenerateProposalPageStore,
      | 'rootStore'
      | '_isLoading'
      | 'proposalDomainModule'
      | '_generatedFilesStatus'
      | '_currentStepIndex'
      | 'steps'
      | 'currentStepController'
      | 'setGeneratedFilesStatus'
      | 'loadInitialData'
    >(this, {
      rootStore: observable,
      _isLoading: observable,
      proposalDomainModule: observable,
      _generatedFilesStatus: observable,
      _currentStepIndex: observable,
      steps: observable,

      areFilesNonExistent: computed,
      isLoading: computed,
      currentStepController: computed,
      isCurrentControllerValid: computed,
      firstStepController: computed,
      secondStepController: computed,
      currentSponsorId: computed,

      setIsLoading: action.bound,
      setGeneratedFilesStatus: action.bound,
      setStepIndex: action.bound,
      onNavigateStepBack: action.bound,
      loadInitialData: action.bound,
      preparePage: action.bound,
      saveStep: action.bound,
      completeFlow: action.bound,
    });
  }
}

const GenerateProposalPageStoreContext = createContext(new GenerateProposalPageStore());

export const useGenerateProposalPageStore = (): GenerateProposalPageStore => {
  return useContext(GenerateProposalPageStoreContext);
};

export { GenerateProposalPageStoreContext };
